import { defineStore } from 'pinia'
import {
  loadCampaign,
  saveCampaign,
  submitCampaign,
  apiCloneCampaign
} from '@/api/campaign.js'
import router from '@/router/index.js'
import { useAlertStore } from './alert'
import { i18n } from '@/main.js'

export const useCampaignStore = defineStore('campaign', {
  state: () => ({
    campaign: {
      prizes: [],
      images: [],
      required_data: [],
      benefit: {},
      short_description: {},
      title: {},
      description: {},
      rules: {},
      tags: [],
      relevant_kid_age: {
        min_months: null,
        max_months: null
      }
    },
    campaignState: {
      errors: [],
      submitted: {},
      is_unfinished: null
    },
    campaignMerchant: {},
    campaignAllowEdit: true,
    currentIdent: null,
    currentStep: 'benefit',
    Cloned: null
  }),

  getters: {
    allValid() {
      return (
        (Object.values(this.campaignState.submitted).every((e) => e === true) &&
          !this.hasErrors) ||
        this.Cloned === this.currentIdent
      )
    },

    campaignType: (state) => state.campaign.type,

    campaignMainType: (state) => state.campaign.type?.split('/')[0],

    availableSteps() {
      return [
        { name: 'benefit', minLevel: 0, next: 'content' },
        { name: 'description', minLevel: 0, next: 'image' },
        { name: 'image', minLevel: 0, next: 'handling' },
        { name: 'handling', minLevel: 0, next: 'review' },
        { name: 'requireddata', minLevel: 1, next: 'review' },
        { name: 'review', minLevel: 0, next: 'review' }
      ]
    },

    nextStep() {
      const step = this.availableSteps.find((s) => s.name === this.currentStep)
      return step?.next
    },

    allErrors: (state) => state.campaignState.errors,

    hasErrors: (state) => state.campaignState.errors.length > 0,

    pageErrors: (state) => (page) => {
      return state.campaignState.errors.filter((e) => e.step === page)
    },

    pageHasError() {
      return (page) => {
        if (this.pageErrors(page).length > 0) {
          return true
        } else if (
          this.pageErrors(page).length > 0 &&
          this.pageSubmitted(page)
        ) {
          return true
        } else {
          return false
        }
      }
    },

    pageIsFinished() {
      return (page) => {
        return !this.pageHasError(page) && this.pageSubmitted(page)
      }
    },

    pageSubmitted: (state) => (page) => {
      return Object.entries(state.campaignState.submitted).find(
        (e) => e[0] === page && e[1] === true
      )
    },

    inCreation: (state) => state.campaignState.is_unfinished,

    prizesLength: (state) => {
      return state.campaign.prizes ? state.campaign.prizes.length : null
    },

    totalWinnerCount: (state) => {
      let total = 0
      if (!state.campaign.prizes) {
        return null
      } else {
        state.campaign.prizes.map((p) => {
          total += p.count * 1
        })
        return total
      }
    },

    totalPrizeValue: (state) => {
      let total = 0
      if (!state.campaign.prizes) {
        return null
      } else {
        state.campaign.prizes.map((p) => {
          total += p.value * p.count
        })
        return total / 100
      }
    }
  },

  actions: {
    async getCampaign(cIdent) {
      const id = cIdent || this.currentIdent

      if (id) {
        const fromLS = this.campaign
        if (fromLS.uuid === id) {
          return fromLS
        } else {
          const fromAPI = await loadCampaign({ ident: id })
          this.campaign = fromAPI.campaign
          this.currentIdent = fromAPI.campaign.uuid
          this.campaignState = fromAPI.state
          this.campaignAllowEdit = fromAPI.allow_edit
          this.campaignMerchant = fromAPI.merchant
          return fromAPI.campaign
        }
      }
    },

    async submitCampaign(cIdent) {
      const rv = await submitCampaign({ ident: cIdent })
      if (rv) {
        this.campaign = rv.campaign
        this.campaignState = rv.state
        this.campaignAllowEdit = rv.allow_edit
        return rv
      }
    },

    async noniteractiveSave(campaign) {
      const cIdent = campaign.uuid
      const rv = await saveCampaign({ ident: cIdent }, campaign)

      this.campaign = rv.campaign
      this.campaignState = rv.state
    },

    async handleSave(campaign) {
      let rv
      const cIdent = campaign.uuid
      const mIdent = this.campaignMerchant.uuid
      const alertStore = useAlertStore()

      let saveMessage
      if (this.allValid) {
        rv = await saveCampaign({ ident: cIdent }, campaign)
        saveMessage = 'save'
      } else {
        rv = await saveCampaign(
          { ident: cIdent, validate: this.currentStep },
          campaign
        )
        saveMessage = 'intermediate'
      }

      this.campaign = rv.campaign
      this.campaignState = rv.state

      if (rv.state.errors.length === 0) {
        alertStore.add({
          type: 'success',
          message: i18n.global.t('campaign.generic.msg_save_' + saveMessage)
        })

        let cRoute
        if (this.campaignMainType === 'sweepstake') {
          if (this.Cloned) {
            cRoute = 'sweepstake-review'
            this.Cloned = null
          } else if (this.inCreation) {
            cRoute = 'sweepstake-' + this.nextStep
          }
        } else if (this.campaignMainType === 'coupon') {
          if (this.Cloned) {
            cRoute = 'coupon-review'
            this.Cloned = null
          } else if (this.inCreation) {
            cRoute = 'coupon-' + this.nextStep
          }
        } else if (this.campaignMainType === 'tip') {
          if (this.Cloned) {
            cRoute = 'tip-review'
            this.Cloned = null
          } else if (this.inCreation) {
            cRoute = 'tip-' + this.nextStep
          }
        }

        // only move to next step if campaign is unfinished
        if (campaign.is_unfinished) {
          router.push({
            name: cRoute,
            params: {
              merchantIdent: mIdent,
              campaignIdent: cIdent
            }
          })
        }
      }
    },

    async setCurrent(ident) {
      if (this.campaign.uuid && this.campaign.uuid !== ident) {
        this.campaign = {
          prizes: [],
          images: [],
          required_data: [],
          benefit: {},
          short_description: {},
          title: {},
          description: {},
          rules: {},
          tags: [],
          relevant_kid_age: {
            min_months: null,
            max_months: null
          }
        }
      }
      if (this.Cloned !== ident) {
        this.Cloned = null
      }
      await this.getCampaign(ident)
      this.currentIdent = ident
    },

    async switchToStep(step) {
      const availableSteps = this.availableSteps
      if (availableSteps.filter((e) => e.name === step)) {
        const newStep = availableSteps.find((e) => e.name === step)
        this.currentStep = newStep.name
      }
    },

    addPrize() {
      this.campaign.prizes.push({
        count: null,
        value: null,
        name: {},
        short_description: {},
        win_needs_response: false,
        winner_instructions: null,
        redemption: null
      })
    },

    removePrize(idx) {
      this.campaign.prizes.splice(idx, 1)
    },

    async doCloneCampaign(campaignIdent) {
      const res = await apiCloneCampaign({
        sourceIdent: campaignIdent
      })

      let cRoute
      if (res.campaign.type.split('/')[0] === 'sweepstake') {
        cRoute = 'sweepstake-handling'
      } else if (res.campaign.type.split('/')[0] === 'coupon') {
        cRoute = 'coupon-handling'
      } else if (res.campaign.type.split('/')[0] === 'tip') {
        cRoute = 'tip-handling'
      }

      this.Cloned = res.campaign.uuid
      await this.setCurrent(res.campaign.uuid)

      const mIdent = this.campaignMerchant.uuid
      router.push({
        name: cRoute,
        params: {
          merchantIdent: mIdent,
          campaignIdent: res.campaign.uuid
        }
      })
    }
  }
})
